import React, { useState, useEffect } from 'react'
import { dataService } from '@/lib/dataService'
import { Plus } from 'lucide-react'

type SessionType = 'pre' | 'post'

interface SessionModalState {
  open: boolean
  editing: any | null
  name: string
  sessionType: SessionType
  startDate: string
  endDate: string
  loading: boolean
}

const INITIAL_MODAL: SessionModalState = {
  open: false,
  editing: null,
  name: '',
  sessionType: 'pre',
  startDate: '',
  endDate: '',
  loading: false
}

const SESSION_TYPE_LABELS: Record<SessionType, { label: string; emoji: string; color: string; bgColor: string; desc: string }> = {
  pre: {
    label: 'ก่อนเรียน (Pre-Test)',
    emoji: '📝',
    color: 'var(--success)',
    bgColor: 'rgba(16, 185, 129, 0.08)',
    desc: 'รอบบันทึกคะแนนก่อนเรียน ครูสามารถกรอกได้เฉพาะช่องคะแนน Pre-Test'
  },
  post: {
    label: 'หลังเรียน (Post-Test)',
    emoji: '✅',
    color: '#d97706',
    bgColor: 'rgba(245, 158, 11, 0.08)',
    desc: 'รอบบันทึกคะแนนหลังเรียน ครูสามารถกรอกได้เฉพาะช่องคะแนน Post-Test'
  }
}

export const AdminSessions: React.FC = () => {
  const [sessions, setSessions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [modal, setModal] = useState<SessionModalState>(INITIAL_MODAL)

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg)
    setTimeout(() => setSuccessMsg(null), 3000)
  }

  const loadSessions = async () => {
    setLoading(true)
    const allSessions = await dataService.getAssessmentSessions()
    setSessions(allSessions)
    setLoading(false)
  }

  useEffect(() => { loadSessions() }, [])

  const handleToggle = async (id: number, currentStatus: boolean) => {
    await dataService.toggleSession(id, !currentStatus)
    showSuccess(currentStatus ? 'ปิดใช้งานรอบคะแนนแล้ว' : 'เปิดใช้งานรอบคะแนนแล้ว')
    loadSessions()
  }

  // Determine session type from session name
  const getSessionType = (name: string): SessionType => {
    const n = name.toLowerCase()
    if (n.includes('post') || n.includes('หลังเรียน')) return 'post'
    return 'pre'
  }

  const openNew = () => {
    const now = new Date()
    const end = new Date(now)
    end.setDate(end.getDate() + 14)
    setModal({
      open: true,
      editing: null,
      name: '',
      sessionType: 'pre',
      startDate: now.toISOString().slice(0, 16),
      endDate: end.toISOString().slice(0, 16),
      loading: false
    })
  }

  const openEdit = (session: any) => {
    const sType = getSessionType(session.name)
    setModal({
      open: true,
      editing: session,
      name: session.name,
      sessionType: sType,
      startDate: new Date(session.start_date).toISOString().slice(0, 16),
      endDate: new Date(session.end_date).toISOString().slice(0, 16),
      loading: false
    })
  }

  const closeModal = () => setModal(INITIAL_MODAL)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setModal(m => ({ ...m, loading: true }))
    try {
      const startIso = new Date(modal.startDate).toISOString()
      const endIso = new Date(modal.endDate).toISOString()

      if (modal.editing) {
        // Edit mode: update dates only (name is fixed)
        await dataService.updateSessionDates(modal.editing.id, startIso, endIso)
        showSuccess('อัปเดตรอบบันทึกคะแนนเรียบร้อยแล้ว')
      } else {
        // Create mode
        const typeConfig = SESSION_TYPE_LABELS[modal.sessionType]
        const sessionName = modal.name.trim() || `${typeConfig.emoji} ${typeConfig.label}`
        await dataService.createAssessmentSession(sessionName, startIso, endIso)
        showSuccess('เพิ่มรอบบันทึกคะแนนใหม่เรียบร้อยแล้ว')
      }
      closeModal()
      loadSessions()
    } catch (err) {
      console.error('Error saving session:', err)
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
      setModal(m => ({ ...m, loading: false }))
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px', height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

      {/* Success toast */}
      {successMsg && (
        <div style={{
          position: 'fixed', top: '24px', right: '24px', zIndex: 1100,
          background: 'var(--success)', color: '#fff',
          padding: '12px 20px', borderRadius: '12px',
          boxShadow: 'var(--shadow-lg)', fontWeight: '700',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          ✓ {successMsg}
        </div>
      )}

      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '6px' }}>⏱️ ตั้งค่ารอบการสอบและกำหนดเวลา</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            กำหนดช่วงเวลากรอกคะแนน (Start/End Date) และเปิด-ปิดระบบป้อนคะแนนสอบรายภาคเรียน
          </p>
        </div>
        <button
          onClick={openNew}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', fontWeight: '700' }}
        >
          <Plus size={16} strokeWidth={3} /> เพิ่มรอบคะแนนใหม่
        </button>
      </div>

      {/* Sessions list */}
      <div className="glassmorphism" style={{ padding: '28px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>รอบคะแนนการสอบทั้งหมด</h2>

        {sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
            <p>ยังไม่มีรอบบันทึกคะแนน กดปุ่ม "เพิ่มรอบคะแนนใหม่" เพื่อเริ่มต้น</p>
          </div>
        ) : (
          <div className="custom-table-container">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>ชื่อรอบคะแนนการสอบ</th>
                  <th>ประเภท</th>
                  <th>วันเริ่มต้น</th>
                  <th>วันสิ้นสุด</th>
                  <th>สถานะระบบ</th>
                  <th>การปรับเปลี่ยน</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => {
                  const sType = getSessionType(session.name)
                  const typeInfo = SESSION_TYPE_LABELS[sType]
                  const isExpired = new Date() > new Date(session.end_date)
                  return (
                    <tr key={session.id}>
                      <td><strong>{session.name}</strong></td>
                      <td>
                        <span style={{
                          background: typeInfo.bgColor,
                          color: typeInfo.color,
                          border: `1px solid ${typeInfo.color}33`,
                          padding: '3px 10px', borderRadius: '20px',
                          fontSize: '12px', fontWeight: '700'
                        }}>
                          {typeInfo.emoji} {sType === 'pre' ? 'Pre-Test' : 'Post-Test'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                        {new Date(session.start_date).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td style={{ color: isExpired ? 'var(--danger)' : 'var(--text-secondary)', fontSize: '13px', fontWeight: isExpired ? '700' : '400' }}>
                        {new Date(session.end_date).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                        {isExpired && <span style={{ marginLeft: '6px', fontSize: '11px' }}>(หมดอายุ)</span>}
                      </td>
                      <td>
                        <span style={{
                          background: session.is_open ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                          border: `1px solid ${session.is_open ? 'var(--success)' : 'var(--danger)'}`,
                          color: session.is_open ? 'var(--success)' : 'var(--danger)',
                          padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600'
                        }}>
                          {session.is_open ? '🟢 เปิดระบบ' : '🔴 ปิดระบบ'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => openEdit(session)}
                            className="btn btn-outline"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            ✏️ แก้ไขช่วงเวลา
                          </button>
                          <button
                            onClick={() => handleToggle(session.id, session.is_open)}
                            className={`btn ${session.is_open ? 'btn-outline' : 'btn-secondary'}`}
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            {session.is_open ? '🔒 ปิดใช้งาน' : '🔓 เปิดใช้งาน'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="glassmorphism" style={{ padding: '20px 24px', borderRadius: '16px', border: '1px solid rgba(99,102,241,0.15)' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>
          ℹ️ วิธีการทำงานของรอบบันทึกคะแนน
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
          {[
            { emoji: '🟢', title: 'รอบ Pre-Test', desc: 'เมื่อเปิดรอบนี้ ครูจะกรอกได้เฉพาะคะแนนก่อนเรียน ช่องหลังเรียนถูกล็อค' },
            { emoji: '🟠', title: 'รอบ Post-Test', desc: 'เมื่อเปิดรอบนี้ ครูจะกรอกได้เฉพาะคะแนนหลังเรียน ช่องก่อนเรียนถูกล็อค' },
            { emoji: '🔴', title: 'ปิดระบบ', desc: 'เมื่อปิดรอบ ครูไม่สามารถแก้ไขคะแนนใดๆ ได้ ต้องขอ Unlock' },
            { emoji: '⚠️', title: 'กฎสำคัญ', desc: 'ควรเปิดรอบเดียวในแต่ละช่วงเวลา ไม่ควรเปิด Pre และ Post พร้อมกัน' }
          ].map((item, i) => (
            <div key={i} style={{ padding: '12px 16px', borderRadius: '10px', background: 'var(--bg-input)' }}>
              <div style={{ fontWeight: '700', marginBottom: '4px', fontSize: '13px' }}>{item.emoji} {item.title}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* === Premium Modal === */}
      {modal.open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal() }}
        >
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '24px',
            padding: '36px',
            width: '100%',
            maxWidth: '560px',
            border: '1px solid var(--border)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
            animation: 'fadeInScale 0.2s ease'
          }}>
            <style>{`@keyframes fadeInScale { from { opacity: 0; transform: scale(0.96) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }`}</style>

            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
              <div>
                <span style={{
                  fontSize: '11px', fontWeight: '800', background: 'rgba(99,102,241,0.08)',
                  color: 'var(--primary)', padding: '3px 10px', borderRadius: '6px',
                  textTransform: 'uppercase', letterSpacing: '1px'
                }}>
                  รอบบันทึกคะแนน
                </span>
                <h2 style={{ fontSize: '22px', fontWeight: '800', margin: '8px 0 4px 0', color: 'var(--text-primary)' }}>
                  {modal.editing ? '✏️ แก้ไขช่วงเวลากรอกคะแนน' : '➕ เพิ่มรอบบันทึกคะแนนใหม่'}
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
                  {modal.editing ? `กำลังแก้ไข: ${modal.editing.name}` : 'ระบบจะใช้ชื่อตามประเภทที่เลือก หรือระบุเองก็ได้'}
                </p>
              </div>
              <button
                onClick={closeModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '22px', lineHeight: 1, padding: '4px' }}
              >×</button>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>

              {/* Session Type Selector (only for new sessions) */}
              {!modal.editing && (
                <div>
                  <label className="input-label">ประเภทรอบคะแนน</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '6px' }}>
                    {(Object.entries(SESSION_TYPE_LABELS) as [SessionType, typeof SESSION_TYPE_LABELS[SessionType]][]).map(([key, val]) => (
                      <div
                        key={key}
                        onClick={() => setModal(m => ({ ...m, sessionType: key, name: '' }))}
                        style={{
                          padding: '14px 16px',
                          borderRadius: '14px',
                          border: `2px solid ${modal.sessionType === key ? val.color : 'var(--border)'}`,
                          background: modal.sessionType === key ? val.bgColor : 'var(--bg-input)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <div style={{ fontWeight: '800', fontSize: '14px', color: modal.sessionType === key ? val.color : 'var(--text-primary)', marginBottom: '4px' }}>
                          {val.emoji} {val.label}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{val.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom name field */}
              <div>
                <label className="input-label" htmlFor="session-name">
                  ชื่อรอบคะแนน {!modal.editing && <span style={{ color: 'var(--text-secondary)', fontWeight: '400' }}>(ไม่บังคับ — ใช้ชื่อตามประเภทถ้าเว้นว่าง)</span>}
                </label>
                <input
                  id="session-name"
                  type="text"
                  className="input-field"
                  value={modal.name}
                  onChange={(e) => setModal(m => ({ ...m, name: e.target.value }))}
                  placeholder={modal.editing ? modal.editing.name : `${SESSION_TYPE_LABELS[modal.sessionType].emoji} ${SESSION_TYPE_LABELS[modal.sessionType].label}`}
                  disabled={!!modal.editing}
                  style={{ opacity: modal.editing ? 0.6 : 1 }}
                />
              </div>

              {/* Date range */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="input-label" htmlFor="session-start">วัน-เวลาเริ่มต้นเปิดระบบ</label>
                  <input
                    id="session-start"
                    type="datetime-local"
                    className="input-field"
                    value={modal.startDate}
                    onChange={(e) => setModal(m => ({ ...m, startDate: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="input-label" htmlFor="session-end">วัน-เวลาปิดระบบ</label>
                  <input
                    id="session-end"
                    type="datetime-local"
                    className="input-field"
                    value={modal.endDate}
                    onChange={(e) => setModal(m => ({ ...m, endDate: e.target.value }))}
                    required
                  />
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button type="button" className="btn btn-outline" onClick={closeModal} disabled={modal.loading}>
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={modal.loading}
                  style={{ minWidth: '140px' }}
                >
                  {modal.loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                      กำลังบันทึก...
                    </span>
                  ) : (
                    modal.editing ? 'บันทึกการแก้ไข' : '➕ เพิ่มรอบคะแนน'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSessions
